import Select, { OptionSelected, SelectProps } from './Select';
import { is } from 'ramda';
import UserGroupDataModel from '../DataModel/UsersDataService';
import { equals } from 'ramda';

import { SupervisedUserCircle, AccountCircle, AccountCircleOutlined  } from "@mui/icons-material";
import {  Chip, TextField, Tooltip, Icon, Box  } from "@mui/material";

interface AuthorityProps extends SelectProps {
	allowSystem: boolean;
	disableExternalGroups: boolean;
	disableExternalUsers: boolean;
	disableNodeRef: Array<string>;
	autoFocus: boolean;
	parentAuthority: string;
	valueParam: string;
	type: string;
	disabled: boolean;
	root: string | null;
	value: any;
	renderOption: any;
	excludedOptions?: any;
}

type Option = { [key: string]: any };

interface AuthorityState {
	inputValue: string;
	loading: boolean;
	matchDisabled: boolean;
	matchSystem: boolean;
	options: Option | Option[] | any;
}

let DataService: UserGroupDataModel;
//
// const makeRequestCreator = () => {
// 	let call: any;
// 	return (url: string, init: boolean | undefined) => {
// 		if (call && !init) call.cancel();
// 		call = axios.CancelToken.source();
// 		return axios.get(url, { cancelToken: call.token });
// 	};
// };
//
// const requestCreator = makeRequestCreator();

class Authority extends React.PureComponent<AuthorityProps, AuthorityState> {
	constructor(props: AuthorityProps) {
		super(props);
		DataService = new UserGroupDataModel();
		this.state = {
			options: [],
			loading: true,
			matchDisabled: false,
			matchSystem: false,
			inputValue: '',
		};
	}

	static defaultProps = {
		valueParam: 'nodeRef',
	};

	componentDidMount() {
		this.getOptions().then(
			() => this.getInputValue
		);
	}

	async componentDidUpdate(prevProps: AuthorityProps, prevState: AuthorityState) {
		if (this.props.type !== prevProps.type || !equals(prevProps.value, this.props.value)) {
			await this.getOptions();
		}
		this.getInputValue(prevProps)
	}

	getInputValue = (prevProps?: AuthorityProps | undefined) => {
		if(this.props.multi) return
		else if(this.props.value?.label !== prevProps?.value?.label) {
			this.setState({ inputValue: this.props.value?.label ?? '' });
		}
		else if(typeof this.props.value == "string" && this.props.value !== prevProps?.value) {
			let val = this.state.options.find(item => item.value === this.props.value)
			this.setState({inputValue: val.label});
		}
	}

	onInputChange = (event: any, newValue: string) => {
			this.setState({ inputValue: newValue });
			this.getOptions(newValue);
	};

	handleChange = (val: any) => {
		const { onChange, multi } = this.props;
		if (typeof onChange === 'function') {
			onChange(val);
		}

		this.setState({
			loading: false,
			inputValue: multi ? '' : val?.label ?? ''
		});

		this.getOptions();
	};

	getOptions = async (input = '') => {
		let { type, parentAuthority, valueParam, allowSystem, root, value } = this.props;

		if (type === 'cm:person' || type === 'USER') type = 'USER';
		else if (type === 'cm:authorityContainer' || type === 'GROUP') type = 'GROUP';
		else type = '';

		const params = {
			type,
			filter: input,
			parentAuthority: parentAuthority || '',
			matchDisabled: this.state.matchDisabled,
			matchSystem: allowSystem,
			root: root || '',
		};
		let options: Option[] | Option | any = await DataService.getUserOptions(value, params);
		let selectedOptions: Option[] = options ? this.mapOptions(options[0], valueParam) : [];
		if (type === '') {
			options = options ? this.mapOptions(options[1].sort(DataService.sortByTypeByName), valueParam) : [];
		} else {
			options = options[1] ? this.mapOptions(options[1], valueParam) : [];
		}
		options = options.concat(selectedOptions);
		this.setState(
			(state) => ({
					...state,
					options: options,
					loading: false,
				}
			)
		);
	};

	getOptionDisabled = (option: any) => {
		const { excludedOptions } = this.props;
		return (
			this.props.disableExternalGroups && option.type === 'GROUP' && (option?.external ?? false) ||
			this.props.disableExternalUsers && option.type === 'USER' && (option?.external ?? false) ||
			this.props?.disableNodeRef?.[0] === option?.uuid ||
			excludedOptions?.includes(option?.value)
		);
	};

	mapOptions = (options: any, valueParam: any) => {
		return options.map((it: any) => ({
			...it,
			label: it?.displayName ?? it.id,
			value: valueParam === 'nodeRef' ? `workspace://SpacesStore/${it.uuid}` : it.id,
			type: it?.type,
		}));
	};

	getDisabled = (event: React.MouseEvent) => {
		event.preventDefault();
		this.getOptions(this.state.inputValue);
		this.setState({ matchDisabled: !this.state.matchDisabled });
	};

	renderTags = (value: any) => value.map((item: any) => {
		let icon;
		let sxStyle;
		switch (item?.type) {
			case 'USER':
				icon = <AccountCircle />;
				sxStyle = { backgroundColor: 'rgb(162, 218, 255)' };
				break;
			case 'GROUP':
				icon = <SupervisedUserCircle />;
				sxStyle = { backgroundColor: 'rgb(255, 208, 123)' };
				break;
			default:
				icon = null;
				sxStyle = {};
		}
		return (
			<Chip
				sx={sxStyle}
				key={item?.value}
				avatar={icon}
				label={item?.label}
				size="small"
				onDelete={this.props.onChange && !this.props.disabled ? () => this.props.onChange(value.filter((it: any) => it?.value !== item?.value)) : undefined}
			/>
		);
	});

	render() {
		const { type, multi } = this.props;
		let value: any;
		if (is(String, this.props?.value)) value = this.state.options.find(item => item.value === this.props.value);
		else if (this.props?.value?.length > 0 && this.props?.value.every((it: any) => typeof it === 'string'))
			value = this.state.options.filter((item: any) => this.props.value.includes(item.value));
		else value = this.props.value;

		return (
			<Select
				{...{ ...this.props }}
				onChange={this.handleChange}
				options={this.state.options}
				getOptionDisabled={this.getOptionDisabled}
				onInputChange={this.onInputChange}
				value={value || []}
				isClearable
				getOptionSelected={(option, value) => {
					return value?.value === option?.value
				}}
				getOptionLabel={(option) => option?.label || option?.displayName || ''}
				groupBy={!this.state.matchDisabled || (type !== 'USER' && type !== 'cm:person' && type !== 'cm:authority') ? null : (option) => option?.enabled ? window.Alfresco.messages.global['edocat.all-workflows.filter.active'] : window.Alfresco.messages.global['edocat.about-page.description.inactive']}
				renderInput={(params: any) => (
					<TextField
						{...params}
						label={this.props?.label ?? ''}
						variant="outlined"
						required={this.props?.mandatory}
						placeholder={this.props?.placeholder || ''}
						error={!!this.props?.error}
						helperText={this.props?.error || this.props?.important || this.props?.helperText}
						InputLabelProps={{ shrink: true }}
						InputProps={{
							...params.InputProps,
							sx: {
								...(['USER', 'cm:person', 'cm:authority'].includes(type) && {
									'&.MuiAutocomplete-inputRoot': {
										paddingRight: '100px !important',
									},
								}),
							},
							endAdornment: (
								<Box sx={{
									position: "absolute",
									right: "9px",
									display: "flex",
									alignItems: 'center',
									'& > :last-of-type': {
										marginLeft: 1,
									},
									'& .MuiAutocomplete-endAdornment': {
										position: 'relative',
										display: 'contents',
									}
								}}>
									{params.InputProps.endAdornment}
									{['USER', 'cm:person', 'cm:authority'].includes(type) && (
										<Tooltip title={this.state.matchDisabled ? window.Alfresco.messages.global['edocat.authority-active'] : window.Alfresco.messages.global['edocat.authority-inactive']}>
											{this.state.matchDisabled ? (
												<AccountCircleOutlined
													sx={{ cursor: this.props.disabled ? 'default' : 'pointer' }}
													onClick={this.props.disabled ? undefined : this.getDisabled}
													color={this.props.disabled ? 'disabled' : 'primary'}
												/>
											) : (
												<AccountCircle
													sx={{ cursor: this.props.disabled ? 'default' : 'pointer' }}
													onClick={this.props.disabled ? undefined : this.getDisabled}
													color={this.props.disabled ? 'disabled' : 'primary'}
												/>
											)}
										</Tooltip>
									)}
								</Box>
							),
						}}
					/>
				)}
				renderTags={multi ? this.renderTags : null}
				renderOption={(props: any, option: any) => (
					<li {...props}>
						<Icon
							sx={{
								fontSize: '.9rem',
								width: '20px',
								textAlign: 'center',
								display: 'inline-block',
								marginRight: '.35rem',
								color:
									option.type === 'GROUP'
										? 'rgb(255, 208, 123)'
										: 'rgb(162, 218, 255)',
							}}
							className={`fas fa-${option.type === 'GROUP' ? 'users' : 'user'}`}
						/>
						{option.label}
					</li>
				)}
			/>
		);
	}
}

export default Authority;